import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';

import { AccountShell } from '~/components/AccountShell';
import { GoogleMark } from '~/components/GoogleMark';
import { UserAvatar } from '~/components/UserAvatar';
import { Select } from '~/components/ui';
import { PHONE_COUNTRY_OPTIONS, dialCodeFor, formatPhoneDigits, parsePhoneDigits } from '~/lib/dialCodes';
import { DEFAULT_PHONE_COUNTRY, RESIDENCY_OPTIONS, setDefaultResidency } from '~/lib/residency';
import { loadTravellerProfile, saveTravellerProfile, travellerFirstName, type TravellerProfile } from '~/lib/travellerProfile';
import { useMember } from '~/stores/useMember';
import { colors, control, fontFamily, radii, space, type } from '~/ui/tokens';

function AccountScreen() {
    const user = useMember(state => state.user);
    const openPrompt = useMember(state => state.openPrompt);
    const { width } = useWindowDimensions();
    const narrow = width < 600;
    const [details, setDetails] = useState<TravellerProfile>({ firstName: '', lastName: '', phoneCountry: DEFAULT_PHONE_COUNTRY, phone: '', nationality: '' });
    const [saveMessage, setSaveMessage] = useState('');

    useEffect(() => {
        if (!user) return;
        const name = user.name.trim().split(/\s+/);
        setDetails(loadTravellerProfile(user.email) ?? {
            firstName: travellerFirstName(user),
            lastName: name.length > 1 ? name.slice(1).join(' ') : '',
            phoneCountry: DEFAULT_PHONE_COUNTRY,
            phone: '',
            nationality: '',
        });
        setSaveMessage('');
    }, [user?.email]);

    function update<K extends keyof TravellerProfile>(key: K, value: TravellerProfile[K]) {
        setDetails(current => ({ ...current, [key]: value }));
        setSaveMessage('');
    }

    function save() {
        if (!user) return;
        if (!details.firstName.trim() || !details.lastName.trim() || !parsePhoneDigits(details.phone, details.phoneCountry).possible || !details.nationality) {
            setSaveMessage('Complete your name, phone number and nationality before saving.');
            return;
        }
        const clean = { ...details, firstName: details.firstName.trim(), lastName: details.lastName.trim() };
        setDefaultResidency(clean.nationality);
        setDetails(clean);
        setSaveMessage(saveTravellerProfile(user.email, clean) ? 'Traveller details saved on this device for your next booking.' : 'Could not save your details on this device. Please try again.');
    }

    return (
        <AccountShell active="account">
            <View style={styles.body}>
                <Text style={styles.heading}>Account</Text>
                <View style={styles.card}>
                    {user ? (
                        <>
                            <View style={styles.identity}>
                                <UserAvatar user={user} size={44} />
                                <View style={styles.identityText}>
                                    <Text style={styles.label}>{user.name}</Text>
                                    <Text style={styles.email} selectable>{user.email}</Text>
                                </View>
                            </View>
                        </>
                    ) : (
                        <View style={styles.signedOut}>
                            <Text style={styles.signInHeading}>Keep your bookings in one place</Text>
                            <Text style={styles.signInDescription}>Sign in to view your trips, booking details and support requests. Membership is free.</Text>
                            <Pressable accessibilityRole="button" onPress={openPrompt} style={styles.signInButton}>
                                <GoogleMark size={19} /><Text style={styles.signInText}>Continue with Google</Text>
                            </Pressable>
                        </View>
                    )}
                </View>
                {user ? <View style={styles.card}>
                    <Text style={styles.formHeading}>Traveller details</Text>
                    <Text style={styles.formDescription}>Save details on this device to fill future bookings faster. You can review or change them at checkout.</Text>
                    <View style={[styles.formRow, narrow && styles.formRowNarrow]}>
                        <View style={styles.field}><Text style={styles.fieldLabel}>First name</Text><TextInput accessibilityLabel="First name" value={details.firstName} onChangeText={value => update('firstName', value)} autoComplete="given-name" style={styles.input} /></View>
                        <View style={styles.field}><Text style={styles.fieldLabel}>Last name</Text><TextInput accessibilityLabel="Last name" value={details.lastName} onChangeText={value => update('lastName', value)} autoComplete="family-name" style={styles.input} /></View>
                    </View>
                    <View style={[styles.formRow, narrow && styles.formRowNarrow]}>
                        <View style={styles.field}><Select value={details.phoneCountry} onChange={value => { update('phoneCountry', value); update('phone', formatPhoneDigits(details.phone, value)); }} options={PHONE_COUNTRY_OPTIONS} displayValue={`+${dialCodeFor(details.phoneCountry)}`} label="Country code" title="Country code" searchable flags /></View>
                        <View style={styles.field}><Text style={styles.fieldLabel}>Phone number</Text><TextInput accessibilityLabel="Phone number" value={details.phone} onChangeText={value => update('phone', formatPhoneDigits(value, details.phoneCountry))} placeholder="Your phone number" placeholderTextColor={colors.inkFaint} keyboardType="phone-pad" autoComplete="tel-national" style={styles.input} /></View>
                    </View>
                    <Select value={details.nationality} onChange={value => update('nationality', value)} options={RESIDENCY_OPTIONS.map(option => ({ value: option.code, label: option.name }))} label="Nationality" placeholder="Select your nationality" title="Nationality" searchable flags />
                    <Pressable accessibilityRole="button" onPress={save} style={styles.saveButton}><Text style={styles.saveButtonText}>Save traveller details</Text></Pressable>
                    {saveMessage ? <Text accessibilityRole="alert" style={styles.saveMessage}>{saveMessage}</Text> : null}
                </View> : null}
            </View>
        </AccountShell>
    );
}

const styles = StyleSheet.create({
    body: { maxWidth: 720, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor, gap: space.card, flexGrow: 1 },
    heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink },
    card: { backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, padding: space.card, gap: space.internal },
    identity: { flexDirection: 'row', alignItems: 'center', gap: space.internal },
    identityText: { flexGrow: 1, flexBasis: 0, minWidth: 0, gap: space.micro },
    label: { fontFamily: fontFamily.semibold, ...type.bodyRegular, color: colors.ink },
    email: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    formHeading: { fontFamily: fontFamily.semibold, ...type.sectionTitle, color: colors.ink },
    formDescription: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    formRow: { flexDirection: 'row', gap: space.internal },
    formRowNarrow: { flexDirection: 'column' },
    field: { flex: 1, minWidth: 0, gap: space.micro },
    fieldLabel: { fontFamily: fontFamily.semibold, ...type.metadata, color: colors.inkSoft },
    input: { minHeight: 48, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: space.internal, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink, backgroundColor: colors.white },
    saveButton: { alignSelf: 'flex-start', backgroundColor: colors.brand, borderRadius: radii.md, paddingVertical: space.compact, paddingHorizontal: space.internal },
    saveButtonText: { fontFamily: fontFamily.semibold, ...control.primaryCta, color: colors.white },
    saveMessage: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
    signedOut: { gap: space.related, alignItems: 'flex-start' },
    signInHeading: { fontFamily: fontFamily.semibold, ...type.sectionTitle, color: colors.ink },
    signInDescription: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft, maxWidth: 440 },
    signInButton: { backgroundColor: colors.white, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingVertical: space.compact, paddingHorizontal: space.internal, marginTop: space.related, alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: space.related },
    signInText: { fontFamily: fontFamily.semibold, ...control.primaryCta, color: colors.ink },
});

export default AccountScreen;
